import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React, { useEffect, useMemo } from 'react';
import { Button, Form, Input, Select, ConfigProvider, message, Checkbox, Alert } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import { useIztro } from 'iztro-hook';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import '../../App.css';
import clinicPosterImage from '../../assets/aijiuguan.png';
import miniProgramCodeImage from '../../assets/baogu.png';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import FourPillars from '../../components/FourPillars';
import HealthLoadingAnimation from '../../components/HealthLoadingAnimation';
import { CITY_OPTIONS, cityFilterOption, type City } from '../../data/cities';
import { calculateDaYunWithAge, type DaYunWithAge, ZIWEI_CONFIG, buildZiWeiData } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

const DEFAULT_ZIWEI_BIRTHDAY = '2000-1-1';

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  latitude?: number;
  currentCity?: string;
  currentLongitude?: number;
  currentLatitude?: number;
  distinguishZiShi?: boolean;
}

function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

function hourToShiChenIndex(hour: number): number {
  return Math.floor((hour + 1) / 2);
}

export const HealthPage = () => {
  const [form] = Form.useForm();
  const reportRef = React.useRef<HTMLDivElement | null>(null);
  const healthReportRef = React.useRef<HTMLDivElement | null>(null);
  const { exportImage } = useExportImage('身心能量报告');

  const [fourPillars, setFourPillars] = React.useState<{ year: string; month: string; day: string; hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>('');

  const [aiLoading, setAiLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError } = useStreamingReport();
  const [healthAnswer, setHealthAnswer] = React.useState<string>('');
  const reportHasContent = Boolean(healthAnswer);

  const [ziWeiBirthday, setZiWeiBirthday] = React.useState<string>('');
  const [ziWeiBirthTime, setZiWeiBirthTime] = React.useState<number>(0);
  const [ziWeiGender, setZiWeiGender] = React.useState<'男' | '女'>('女');

  const { astrolabe } = useIztro({
    birthday: ziWeiBirthday || DEFAULT_ZIWEI_BIRTHDAY,
    birthTime: ziWeiBirthTime,
    gender: ziWeiGender,
    birthdayType: 'solar',
    fixLeap: true,
    lang: 'zh-CN',
    ...(ZIWEI_CONFIG as any),
  });

  const ziWeiData = useMemo(() => {
    if (!astrolabe || !ziWeiBirthday || ziWeiBirthday === DEFAULT_ZIWEI_BIRTHDAY) {
      return null;
    }
    return buildZiWeiData(astrolabe, ziWeiBirthTime);
  }, [astrolabe, ziWeiBirthday, ziWeiBirthTime]);

  const cityOptions = React.useMemo(() => CITY_OPTIONS, []);

  const handleCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude,
      latitude: selectedCity.latitude,
    });
  };

  const handleCurrentCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      currentLongitude: selectedCity.longitude,
      currentLatitude: selectedCity.latitude,
    });
  };

  const performPaiPan = (birthInfo: {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    solar?: string;
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean;
  }) => {
    let inputYear: number;
    let inputMonth: number;
    let inputDay: number;
    let inputHour: number;
    let inputMinute: number;

    if (birthInfo.solar) {
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }

    const { longitude, gender, distinguishZiShi = false } = birthInfo;
    const standard = dayjs(
      `${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay
        .toString()
        .padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute
          .toString()
          .padStart(2, '0')}:00`,
    );
    const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);

    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();

    const isInZiShi = standardHour === 23 || standardHour === 0;
    if (isInZiShi && !distinguishZiShi && standardHour === 23) {
      const nextDay = standard.add(1, 'day');
      standardYear = nextDay.year();
      standardMonth = nextDay.month() + 1;
      standardDay = nextDay.date();
    }

    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();

    setTrueSolarTimeStr(trueSolar.format('YYYY年MM月DD日 HH:mm'));

    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();

    const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue.getLunarHour();

    // 年柱按立春时刻精确切换
    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();

    try {
      const currentJieQi = lunarObj.getCurrentJieQi();

      if (currentJieQi && currentJieQi.isJie()) {
        const jieQiSolar = currentJieQi.getSolar();
        const birthTimestamp = standard.valueOf();
        const jieQiTimestamp = new Date(
          jieQiSolar.getYear(),
          jieQiSolar.getMonth() - 1,
          jieQiSolar.getDay(),
          jieQiSolar.getHour(),
          jieQiSolar.getMinute(),
          jieQiSolar.getSecond(),
        ).getTime();

        if (birthTimestamp < jieQiTimestamp) {
          const prevJieQi = lunarObj.getPrevJieQi();
          if (prevJieQi) {
            const prevJieQiSolar = prevJieQi.getSolar();
            const prevSolar = Solar.fromYmd(
              prevJieQiSolar.getYear(),
              prevJieQiSolar.getMonth(),
              prevJieQiSolar.getDay(),
            );
            sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
          }
        }
      }
    } catch (error) {
      console.error('月柱节气精确判断失败，使用默认值:', error);
    }

    setLunarDateStr(
      `${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${lunarHourTrue
        .getSixtyCycle()
        .getName()}时`,
    );

    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });

    const daYunList = calculateDaYunWithAge(
      standardYear,
      standardMonth,
      standardDay,
      standardHour,
      standardMinute,
      sixtyCycleYear,
      sixtyCycleMonth,
      gender,
      8,
    );
    setDaYun(daYunList);

    let validHour = Math.floor(trueSolarHour);
    if (validHour < 0) validHour = 0;
    if (validHour > 23) validHour = 23;

    const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const shiChenIndex = hourToShiChenIndex(validHour);

    setZiWeiBirthday(birthdayStr);
    setZiWeiBirthTime(shiChenIndex);
    setZiWeiGender(gender === 'male' ? '男' : '女');
  };

  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error('请选择出生日期和时间');
      return;
    }

    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);
    if (Number.isNaN(longitudeNum)) {
      message.error('请输入有效的经度，例如 116.4');
      return;
    }

    const standard = date.second(0).millisecond(0);
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false,
    });

    setHealthAnswer('');
  };

  const generateHealthReport = async () => {
    if (!fourPillars || daYun.length === 0) {
      message.error('请先完成排盘');
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, currentCity, currentLongitude, currentLatitude } = values;

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city,
      longitude,
      latitude,
      currentCity,
      currentLongitude,
      currentLatitude,
      gender,
    };

    setAiLoading(true);
    try {
      const resp = await runReportStream('/api/bazi-health', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          birthInfo,
          ziWeiData,
        }),
      }, (text) => { setHealthAnswer(text); setAiLoading(false); });

      if (!resp.ok) {
        let errorMessage = '健康报告生成失败';
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore json parse error
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      setHealthAnswer(data.answer || '暂无报告内容');
      message.success('身心能量报告已生成');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '生成失败，请稍后重试');
    } finally {
      setAiLoading(false);
    }
  };


  const onReset = () => {
    form.resetFields();
    setFourPillars(null);
    setDaYun([]);
    setLunarDateStr('');
    setTrueSolarTimeStr('');
    setHealthAnswer('');
  };

  useEffect(() => {
    form.setFieldsValue({
      birthDateTime: { date: dayjs(), isLunar: false },
      gender: '',
    });
  }, [form]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 300); // 等待页面渲染完成

    return () => clearTimeout(timer);
  }, [reportHasContent]);

  return (
    <ConfigProvider locale={zhCN}>
      <HealthLoadingAnimation visible={aiLoading} />
      <div className="app-container">
        <div className="app-layout">
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              {/* <div className="hero-section" style={{ marginBottom: '24px' }}>
                <h1 className="hero-title">身心能量体检</h1>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <Button onClick={() => navigate('/')}>返回首页</Button>
                  <Button type="primary" onClick={() => document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth' })}>
                    立即开始
                  </Button>
                </div>
              </div> */}

              <div className="form-card">
                <h1 className="form-title">身心能量体检</h1>
                <h2 className="hero-subtitle">以五运六气为解释框架，输出可执行的日常养护策略</h2>
                <Form form={form} name="health-form" onFinish={onFinish} layout="vertical">
                  <Form.Item
                    name="birthDateTime"
                    label="出生时间"
                    rules={[{ required: true, message: '请选择出生时间' }]}
                  >
                    <BirthDateTimePicker />
                  </Form.Item>

                  <Alert
                    className="health-time-hint"
                    type="info"
                    style={{ marginBottom: '16px' }}
                    message="温馨提示：如果不确定具体分钟，只知道出生时辰也可以"
                    description={
                      <div>
                        <div>比如只知道“巳时/午时”，先按大致时间填写即可。后续可用相邻两个时辰各测一次，对比哪份建议更贴近日常体感，再作为你的长期实践方向。</div>
                        <div style={{ marginTop: 8 }}>
                          时辰对照：子时23:00-00:59，丑时01:00-02:59，寅时03:00-04:59，卯时05:00-06:59，辰时07:00-08:59，巳时09:00-10:59，午时11:00-12:59，未时13:00-14:59，申时15:00-16:59，酉时17:00-18:59，戌时19:00-20:59，亥时21:00-22:59。
                        </div>
                      </div>
                    }
                  />

                  <Form.Item
                    name="gender"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别' }]}
                  >
                    <Select
                      placeholder="请选择性别"
                      options={[
                        { value: 'male', label: '男' },
                        { value: 'female', label: '女' },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    label="出生城市"
                    rules={[{ required: true, message: '请选择出生城市' }]}
                  >
                    <Select
                      showSearch
                      placeholder="请选择城市"
                      options={cityOptions}
                      onChange={handleCityChange}
                      optionFilterProp="searchText"
                      filterOption={cityFilterOption}
                    />
                  </Form.Item>

                  <Form.Item
                    name="currentCity"
                    label="当前所在城市"
                    rules={[{ required: true, message: '请选择当前所在城市' }]}
                    tooltip="用于地域气候修正（你现在长期生活的地方）"
                  >
                    <Select
                      showSearch
                      placeholder="请选择当前所在城市"
                      options={cityOptions}
                      onChange={handleCurrentCityChange}
                      optionFilterProp="searchText"
                      filterOption={cityFilterOption}
                    />
                  </Form.Item>

                  {/* 隐藏字段：经度值由城市选择自动填充，用于提交 */}
                  <Form.Item name="longitude" hidden>
                    <Input type="number" />
                  </Form.Item>

                  <Form.Item name="currentLongitude" hidden>
                    <Input type="number" />
                  </Form.Item>

                  <Form.Item name="distinguishZiShi" valuePropName="checked">
                    <Checkbox>区分早晚子时</Checkbox>
                  </Form.Item>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <Button type="primary" htmlType="submit" className="submit-button">
                      开始排盘
                    </Button>
                    <Button htmlType="button" onClick={onReset} className="reset-button">
                      重置
                    </Button>
                  </div>
                </Form>
              </div>

              {fourPillars && (
                <div className="result-card" style={{ marginTop: '24px' }}>
                  <h2 className="result-title">排盘结果</h2>
                  <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
                    <p>公历（北京时间）：{form.getFieldValue('birthDateTime')?.date?.format('YYYY年MM月DD日 HH:mm')}</p>
                    <p style={{ color: '#d4af6a', fontWeight: 500, fontSize: '14px', margin: '8px 0' }}>真太阳时：{trueSolarTimeStr}</p>
                    <p>农历：{lunarDateStr}</p>
                  </div>
                  <FourPillars {...fourPillars} />

                  {/* {daYun.length > 0 && (
                    <div className="dayun-section" style={{ marginTop: '20px' }}>
                      <h3 className="result-subtitle">阶段趋势</h3>
                      <div className="dayun-list">
                        {daYun.map((item) => (
                          <div key={item.index} className="dayun-item">
                            <span className="dayun-label">
                              第{item.index}运：{item.ganZhi}
                            </span>
                            <span className="dayun-info">
                              ({item.startAge} - {item.endAge}岁，{item.startYear}年 - {item.endYear}年)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )} */}

                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button type="primary" size="large" className="submit-button" disabled={reportStreaming} onClick={generateHealthReport}>
                      生成我的身心能量报告
                    </Button>
                  </div>
                </div>
              )}

              <StreamingStatus active={reportStreaming && Boolean(healthAnswer)} error={streamError} />
              {healthAnswer && (
                <div ref={reportRef} className="result-card" style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div ref={healthReportRef}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 className="result-title" style={{ margin: 0 }}>身心能量报告</h2>
                      <Button
                        type="primary"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.health-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          fontWeight: 600
                        }}
                      >
                        🖼️ 保存为图片
                      </Button>
                    </div>

                    {/* 保存提示 */}
                    <div style={{
                      margin: '0 0 20px',
                      padding: '14px 18px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 175, 106, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{ fontSize: '24px' }}>💡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#d4af6a', marginBottom: '4px' }}>
                          报告内容较长，建议先保存再慢慢品读
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          点击"保存为图片"按钮，将报告保存到相册，方便随时查看
                        </div>
                      </div>
                    </div>

                    <div className="markdown-content health-report-content" style={{ color: '#2f2f2f' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{healthAnswer}</ReactMarkdown>
                      
                      {/* 二维码区域 - 包含在导出范围内 */}
                      <div
                        style={{
                          marginTop: '32px',
                          paddingTop: '24px',
                          borderTop: '1px solid rgba(212, 175, 106, 0.28)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '20px',
                          }}
                        >
                          <div style={{ flex: '1 1 320px', maxWidth: '420px' }}>
                            <img
                              src={clinicPosterImage}
                              alt="鲍姑艾灸馆介绍二维码"
                              style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: '16px',
                                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                              }}
                            />
                          </div>
                          <div style={{ flex: '0 1 260px', width: '100%', maxWidth: '260px' }}>
                            <img
                              src={miniProgramCodeImage}
                              alt="鲍姑艾灸馆小程序二维码"
                              style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: '16px',
                                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 底部保存按钮 - 不包含在导出范围内 */}
                    <div style={{
                      marginTop: '40px',
                      padding: '32px 24px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
                      borderRadius: '16px',
                      border: '2px solid rgba(212, 175, 106, 0.2)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#333',
                        marginBottom: '8px'
                      }}>
                        感谢您阅读完整报告
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: '1.7',
                        marginBottom: '20px'
                      }}>
                        点击"保存为图片"，将这份珍贵的身心能量报告保存到相册，<br />
                        随时回顾，持续践行健康生活方式。
                      </p>
                      <Button
                        type="primary"
                        size="large"
                        disabled={reportStreaming || Boolean(streamError)} onClick={() => exportImage('.health-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          height: '48px',
                          padding: '0 40px',
                          fontSize: '16px',
                          fontWeight: 600,
                          boxShadow: '0 6px 20px rgba(212, 175, 106, 0.3)'
                        }}
                      >
                        📸 立即保存长图
                      </Button>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default HealthPage;
